/**
 * Procesador de FACTURAS EMITIDAS desde PDF: sube los PDFs de venta, la IA
 * extrae cliente / desglose / totales, se aplican las cuentas del maestro de
 * clientes y se exporta el SUENLACE.DAT (+ Excel resumen) en un ZIP.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FileText,
  Download,
  Loader2,
  Building2,
  LogOut,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Trash2,
  Search,

} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { recordProcessingEvent } from "@/lib/processingMetrics";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import MasterSelector from "@/components/MasterSelector";
import ThemeToggle from "@/components/ThemeToggle";
import DropZone from "@/components/DropZone";
import QuickCustomerDialog from "@/components/emitidas/QuickCustomerDialog";
import IssuedInvoiceSplitView from "@/components/emitidas/IssuedInvoiceSplitView";
import IssuedAIValidator from "@/components/emitidas/IssuedAIValidator";
import { loadCompanyAccountCatalog, type CompanyAccountRow } from "@/lib/companyAccountCatalog";
import { loadCompanyMasters, type CompanyMaster } from "@/lib/supplierMaster";
import {
  loadCustomerMaster,
  normalizeNif,
  type CustomerMasterEntry,
  type CustomerMasterRecord,
} from "@/lib/customerMaster";
import { extractIssuedPdf, imageFileToBase64, isImageFile } from "@/lib/issuedPdfExtract";
import {
  applyCustomerMaster,
  applySeriesRules,
  buildIssuedFileName,
  toIssuedRow,
  validateIssuedDoc,
  type IssuedFailedFile,
  type IssuedProcessedFile,
} from "@/lib/issuedProcessing";
import { buildIssuedSuenlaceFile } from "@/lib/a3ecoExportIssued";
import { downloadFilesAsZip } from "@/lib/pdfDownload";
import { formatMoney, parseNumericLike } from "@/lib/numberFormatting";
import { useIssuedSession } from "@/hooks/useIssuedSession";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { sortByInvoiceNumber } from "@/lib/invoiceSorting";
import { detectCreditNoteHints } from "@/lib/creditNoteDetection";
import GlobalSearch, { type SearchableItem } from "@/components/GlobalSearch";
import DocStatusFilter, { type DocStatusFilterValue } from "@/components/DocStatusFilter";
import { Input } from "@/components/ui/input";

import SaveIndicator from "@/components/SaveIndicator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";


const CONCURRENCY = 3;

const buildExcelSummary = async (docs: IssuedProcessedFile[]): Promise<Blob> => {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  const sheet = wb.addWorksheet("Facturas emitidas");
  sheet.columns = [
    { header: "Fecha", key: "date", width: 12 },
    { header: "Serie", key: "series", width: 10 },
    { header: "Nº factura", key: "number", width: 16 },
    { header: "Cliente", key: "customer", width: 34 },
    { header: "NIF", key: "nif", width: 12 },
    { header: "Cuenta cliente", key: "customerAccount", width: 14 },
    { header: "Cuenta venta", key: "salesAccount", width: 14 },
    { header: "Base", key: "base", width: 12 },
    { header: "IVA", key: "vat", width: 12 },
    { header: "IRPF", key: "irpf", width: 12 },
    { header: "Total", key: "total", width: 12 },
    { header: "Archivo", key: "file", width: 40 },
  ];
  sheet.getRow(1).font = { bold: true };

  for (const d of docs) {
    const base = d.vatLines.reduce((s, l) => s + parseNumericLike(l.taxableBase), 0);
    const vat = d.vatLines.reduce((s, l) => s + parseNumericLike(l.vatAmount), 0);
    sheet.addRow({
      date: d.date,
      series: d.series,
      number: d.invoiceNumber,
      customer: d.customerName,
      nif: d.customerTaxId,
      customerAccount: d.customerAccount,
      salesAccount: d.salesAccount,
      base: Number(base.toFixed(2)),
      vat: Number(vat.toFixed(2)),
      irpf: Number(parseNumericLike(d.irpfAmount).toFixed(2)),
      total: Number(parseNumericLike(d.totalAmount).toFixed(2)),
      file: d.file.name,
    });
  }

  const last = docs.length + 1;
  const totalsRow = sheet.addRow({ date: "TOTALES" });
  for (const col of ["H", "I", "J", "K"]) {
    totalsRow.getCell(col).value = { formula: `SUM(${col}2:${col}${last})` } as never;
  }
  totalsRow.font = { bold: true };
  for (const col of ["H", "I", "J", "K"]) {
    sheet.getColumn(col).numFmt = "#,##0.00";
  }

  const buffer = await wb.xlsx.writeBuffer();
  return new Blob([buffer as ArrayBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

const ProcesarPdf = () => {
  const { user, signOut } = useAuth();
  const { toast } = useToast();

  const [companies, setCompanies] = useState<CompanyMaster[]>([]);
  const [company, setCompany] = useState<CompanyMaster | null>(null);
  const [pendingCompanyChange, setPendingCompanyChange] = useState<CompanyMaster | null>(null);
  const companyRef = useRef<CompanyMaster | null>(null);
  companyRef.current = company;
  const [showCompanySelector, setShowCompanySelector] = useState(false);

  const [master, setMaster] = useState<Map<string, CustomerMasterEntry>>(new Map());
  const [accountCatalog, setAccountCatalog] = useState<CompanyAccountRow[]>([]);
  const [processed, setProcessed] = useState<IssuedProcessedFile[]>([]);
  const [failed, setFailed] = useState<IssuedFailedFile[]>([]);
  const [pending, setPending] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloadingPdfs, setDownloadingPdfs] = useState(false);
  const [confirmFinalize, setConfirmFinalize] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<DocStatusFilterValue>("all");
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);


  const {
    loading: sessionLoading,
    initialState,
    saveSession,
    clearSession,
    saveStatus,
    lastSavedAt,
  } = useIssuedSession(user?.id);

  const issuerTaxIdRef = useRef<string>("");
  issuerTaxIdRef.current = company?.tax_id ? normalizeNif(company.tax_id) : "";
  const masterRef = useRef(master);
  masterRef.current = master;
  const userIdRef = useRef<string | undefined>(undefined);
  userIdRef.current = user?.id;
  const companyCheckRef = useRef<string>("");
  const isResettingCompanyRef = useRef(false);
  // La empresa elegida por el usuario siempre prevalece sobre la empresa
  // pendiente de restaurar desde una sesión anterior.
  const companySelectedManuallyRef = useRef(false);

  useEffect(() => {
    if (!user?.id) return;
    loadCompanyMasters().then(setCompanies);
  }, [user?.id]);

  const [refreshingMaster, setRefreshingMaster] = useState(false);

  // Catálogo de contrapartidas de la empresa activa (desplegable de cuenta de venta).
  useEffect(() => {
    if (!company?.id) {
      setAccountCatalog([]);
      return;
    }
    let cancelled = false;
    loadCompanyAccountCatalog(company.id).then((rows) => {
      if (!cancelled) setAccountCatalog(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [company?.id]);

  /**
   * Recarga el maestro de clientes y reaplica las cuentas a los documentos que
   * todavía no las tienen (p. ej. si el cliente se ha dado de alta o importado
   * después de haber leído la factura).
   */
  const refreshMaster = useCallback(async (companyId?: string) => {
    const map = await loadCustomerMaster(companyId);
    setMaster(map);
    setProcessed((prev) =>
      prev.map((d) => {
        // Respeta las correcciones manuales: la recarga del maestro solo
        // completa documentos que el usuario no ha tocado.
        if (d.manuallyEdited) return d;
        const applied = applyCustomerMaster(d, map);
        return applied.masterApplied
          ? applySeriesRules(applied, issuerTaxIdRef.current)
          : { ...d, masterApplied: false };
      }),
    );
    return map;
  }, []);

  const handleRefreshMaster = useCallback(async () => {
    if (!company?.id) return;
    setRefreshingMaster(true);
    try {
      const map = await refreshMaster(company.id);
      toast({
        title: "Datos actualizados",
        description: `Maestro de clientes recargado (${map.size} clientes).`,
      });
    } finally {
      setRefreshingMaster(false);
    }
  }, [company?.id, refreshMaster, toast]);

  // Al volver a la pestaña se recarga el maestro por si se han añadido clientes
  // desde "Datos de empresa" u otra sección.
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === "visible" && company?.id) void refreshMaster(company.id);
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [company?.id, refreshMaster]);


  // Rehidratar la sesión guardada una sola vez.
  useEffect(() => {
    if (sessionLoading || !initialState || isHydrated) return;
    setProcessed(initialState.processed);
    setFailed(initialState.failed);
    setIsHydrated(true);
  }, [sessionLoading, initialState, isHydrated]);

  // Restaurar la empresa guardada solo si el usuario todavía no ha elegido
  // ninguna. La actualización funcional comprueba el valor más reciente y
  // evita que una carga tardía de la lista sobrescriba una selección manual.
  useEffect(() => {
    if (!isHydrated || !initialState?.companyId || companies.length === 0) return;
    const savedCompany = companies.find((c) => c.id === initialState.companyId);
    if (!savedCompany) return;
    setCompany((current) => {
      if (current || companySelectedManuallyRef.current) return current;
      return savedCompany;
    });
  }, [isHydrated, initialState?.companyId, companies]);

  // Autoguardado en la nube (debounce interno de 1,5 s).
  // Si no hay documentos en curso no persistimos la empresa: al volver a la
  // pantalla se pedirá seleccionarla de nuevo en lugar de restaurar una
  // selección "vacía".
  useEffect(() => {
    if (!user?.id || sessionLoading || !isHydrated || pending > 0 || isResettingCompanyRef.current) return;
    const hasDocuments = processed.length > 0 || failed.length > 0;
    void saveSession({
      processed,
      failed,
      companyId: hasDocuments ? companyRef.current?.id ?? null : null,
    });
  }, [user?.id, sessionLoading, isHydrated, pending, processed, failed, company?.id, saveSession]);

  useEffect(() => {
    if (!company?.id) {
      setMaster(new Map());
      return;
    }
    refreshMaster(company.id);
    // Conservamos los documentos al corregir la empresa seleccionada y
    // reaplicamos siempre el maestro de la nueva empresa. Antes se vaciaban,
    // lo que impedía recuperar una sesión asociada por error a otra empresa.
  }, [company?.id, refreshMaster]);

  // Si ningún NIF de los documentos encaja en el maestro de la empresa
  // seleccionada, avisamos, pero NUNCA cambiamos la empresa: un cliente puede
  // existir en el maestro de otra empresa (p. ej. clientes compartidos) y
  // cambiarla automáticamente falseaba el código de empresa en el traspaso.
  useEffect(() => {
    if (!company?.id || processed.length === 0 || companies.length === 0) return;
    const taxIds = [...new Set(processed.map((d) => normalizeNif(d.customerTaxId)).filter(Boolean))];
    if (taxIds.length === 0 || taxIds.some((taxId) => master.has(taxId))) return;

    const checkKey = `${company.id}:${taxIds.sort().join(",")}`;
    if (companyCheckRef.current === checkKey) return;
    companyCheckRef.current = checkKey;

    toast({
      title: "Clientes no encontrados en el maestro",
      description: `Ningún NIF de estas facturas está dado de alta en el maestro de ${company.name}. Revisa la empresa seleccionada o añade los clientes.`,
    });
  }, [companies, company?.id, company?.name, master, processed, toast]);


  const handleFinalize = async () => {
    setCompany(null);
    setProcessed([]);
    setFailed([]);
    setOpenId(null);
    setPending(0);
    await clearSession();
    toast({ title: "Sesión finalizada", description: "Se han vaciado las facturas emitidas en curso y se ha cerrado la empresa seleccionada." });
  };


  const makeId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  const processOne = useCallback(async (file: File) => {
    try {
      let images: string[];
      let text: string | undefined;
      if (isImageFile(file)) {
        images = await imageFileToBase64(file);
      } else {
        const result = await extractIssuedPdf(file);
        images = result.images;
        const raw = result.text.trim();
        text = raw ? raw.slice(0, 4000) : undefined;
      }

      let last: { data: any; error: any } = { data: null, error: null };
      let refreshed = false;
      for (let attempt = 0; attempt < 3; attempt++) {
        const result = await supabase.functions.invoke("extract-issued-invoice", {
          body: { images, text, issuerTaxId: issuerTaxIdRef.current, companyId: companyRef.current?.id ?? null },
        });
        last = result;
        if (!result.error) break;
        const status = (result.error as any)?.context?.status;
        if (status === 401 && !refreshed) {
          refreshed = true;
          try { await supabase.auth.refreshSession(); } catch { /* ignore */ }
          continue;
        }
        const msg = result.error?.message || "";
        const transient = status === 429 || /rate limit|failed to fetch|network|load failed/i.test(msg);
        if (!transient || attempt === 2) break;
        await new Promise((r) => setTimeout(r, 1500 * Math.pow(2, attempt)));
      }

      if (last.error) {
        const status = (last.error as any)?.context?.status;
        let detail = last.error.message || "desconocido";
        if (status === 402) detail = "Saldo de IA agotado.";
        else if (status === 429) detail = "El servicio de IA está saturado. Reintenta en unos segundos.";
        setFailed((prev) => [...prev, { id: makeId(), file, reason: detail, kind: "error" }]);
        return;
      }
      const data = last.data;
      if (!data) {
        setFailed((prev) => [...prev, { id: makeId(), file, reason: "Respuesta vacía del servidor", kind: "error" }]);
        return;
      }
      if (data.isInvoice === false) {
        setFailed((prev) => [...prev, {
          id: makeId(),
          file,
          reason: `Parece un ${data.documentType || "documento"} y no una factura.`,
          kind: "rejected",
        }]);
        return;
      }

      const vatLines = (Array.isArray(data.vatBreakdown) ? data.vatBreakdown : [])
        .map((l: any) => ({
          taxableBase: parseNumericLike(l?.taxableBase),
          vatRate: parseNumericLike(l?.vatRate),
          vatAmount: parseNumericLike(l?.vatAmount),
          reRate: parseNumericLike(l?.reRate) || undefined,
          reAmount: parseNumericLike(l?.reAmount) || undefined,
        }))
        .filter((l: any) => l.taxableBase !== 0 || l.vatAmount !== 0);

      // Segunda comprobación sobre el texto del documento: si es un abono y
      // la IA no lo marcó, invertimos los signos aquí.
      const creditHints = detectCreditNoteHints(text, String(data.documentType || ""));
      const isCredit = !!data.isNegativeInvoice || creditHints.strong;
      // Si es abono, todos los importes quedan en negativo pase lo que pase.
      const neg = (n: number) => (isCredit ? -Math.abs(n) : n);
      const signedLines = isCredit
        ? vatLines.map((l: any) => ({
            ...l,
            taxableBase: -Math.abs(l.taxableBase),
            vatAmount: -Math.abs(l.vatAmount),
            reAmount: l.reAmount ? -Math.abs(l.reAmount) : l.reAmount,
          }))
        : vatLines;

      const base: IssuedProcessedFile = {
        id: makeId(),
        file,
        date: String(data.date || ""),
        series: String(data.series || ""),
        invoiceNumber: String(data.invoiceNumber || ""),
        customerName: String(data.customerName || ""),
        customerTaxId: normalizeNif(String(data.customerTaxId || "")),
        customerAccount: "",
        salesAccount: "",
        postalCode: String(data.postalCode || "").replace(/\D/g, "").slice(0, 5),
        vatLines: signedLines.length > 0 ? signedLines : [{ taxableBase: 0, vatRate: 21, vatAmount: 0 }],
        irpfRate: parseNumericLike(data.irpfRate),
        irpfAmount: neg(parseNumericLike(data.irpfAmount)),
        totalAmount: neg(parseNumericLike(data.totalAmount)),
        paymentMethod: String(data.paymentMethod || ""),
        masterApplied: false,
        isCreditNote: isCredit,
        creditNoteHints: creditHints.matched,
        creditNoteHintLevel: creditHints.strong ? "strong" : creditHints.weak ? "weak" : "none",
      };

      setProcessed((prev) => [...prev, applySeriesRules(applyCustomerMaster(base, masterRef.current), issuerTaxIdRef.current)]);
    } catch (e) {
      setFailed((prev) => [...prev, {
        id: makeId(),
        file,
        reason: e instanceof Error ? e.message : "Error inesperado leyendo el documento",
        kind: "error",
      }]);
      if (userIdRef.current) {
        void recordProcessingEvent({
          userId: userIdRef.current,
          eventType: "failed",
          count: 1,
          companyId: companyRef.current?.id ?? null,
        });
      }
    }
  }, []);

  const handleFiles = useCallback(async (files: File[]) => {
    if (!company) {
      toast({ title: "Elige primero una empresa", variant: "destructive" });
      return;
    }
    const valid = files.filter((f) => /\.(pdf|png|jpe?g)$/i.test(f.name));
    if (valid.length === 0) {
      toast({ title: "Formatos admitidos: PDF, PNG y JPEG", variant: "destructive" });
      return;
    }
    setPending((n) => n + valid.length);
    const queue = [...valid];
    const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
      while (queue.length > 0) {
        const next = queue.shift();
        if (!next) break;
        await processOne(next);
        setPending((n) => Math.max(0, n - 1));
      }
    });
    await Promise.all(workers);
  }, [company, processOne, toast]);

  const updateDoc = (id: string, patch: Partial<IssuedProcessedFile>) => {
    setProcessed((prev) => prev.map((d) => {
      if (d.id !== id) return d;
      const touchesAccounts =
        patch.customerAccount !== undefined ||
        patch.salesAccount !== undefined ||
        patch.customerName !== undefined ||
        patch.postalCode !== undefined;
      const next = {
        ...d,
        ...patch,
        reviewed: true,
        ...(touchesAccounts ? { manuallyEdited: true } : {}),
      };
      // El aviso de abono sigue el signo de los importes: al ponerlos en
      // positivo se quita solo; al ponerlos en negativo vuelve a salir.
      if (
        patch.vatLines !== undefined ||
        patch.irpfAmount !== undefined ||
        patch.totalAmount !== undefined
      ) {
        const anyNegative =
          next.vatLines.some(
            (l) =>
              parseNumericLike(l.taxableBase) < 0 ||
              parseNumericLike(l.vatAmount) < 0 ||
              parseNumericLike(l.reAmount ?? 0) < 0,
          ) ||
          parseNumericLike(next.totalAmount) < 0 ||
          parseNumericLike(next.irpfAmount) < 0;
        if (!!d.isCreditNote !== anyNegative) next.creditNoteReviewed = true;
        next.isCreditNote = anyNegative;
      }
      if (patch.customerTaxId !== undefined) {
        return applySeriesRules(applyCustomerMaster(next, masterRef.current), issuerTaxIdRef.current);
      }
      if (patch.series !== undefined || patch.invoiceNumber !== undefined) {
        return applySeriesRules(next, issuerTaxIdRef.current);
      }
      return next;
    }));
  };

  const openDoc = (id: string) => {
    setOpenId(id);
    setProcessed((prev) => prev.map((d) => (d.id === id ? { ...d, reviewed: true } : d)));
  };

  const deleteProcessed = (id: string) => {
    setProcessed((prev) => {
      const next = prev.filter((d) => d.id !== id);
      setOpenId((cur) => (cur === id ? null : cur));
      return next;
    });
    toast({ title: "Documento eliminado" });
  };


  const retryFailed = async (item: IssuedFailedFile) => {
    setFailed((prev) => prev.filter((f) => f.id !== item.id));
    setPending((n) => n + 1);
    await processOne(item.file);
    setPending((n) => Math.max(0, n - 1));
  };

  const errorsByDoc = useMemo(
    () => processed.map((d) => validateIssuedDoc(d)),
    [processed],
  );
  const totalErrors = errorsByDoc.reduce((s, e) => s + e.length, 0);

  const totals = useMemo(() => {
    let base = 0, vat = 0, total = 0;
    for (const d of processed) {
      base += d.vatLines.reduce((s, l) => s + parseNumericLike(l.taxableBase), 0);
      vat += d.vatLines.reduce((s, l) => s + parseNumericLike(l.vatAmount), 0);
      total += parseNumericLike(d.totalAmount);
    }
    return { base, vat, total };
  }, [processed]);

  const processedSorted = useMemo(() => sortByInvoiceNumber(processed), [processed]);

  const statusCounts = useMemo(() => {
    const pending = processed.filter((d) => validateIssuedDoc(d).length > 0).length;
    return { all: processed.length, pending, ready: processed.length - pending };
  }, [processed]);

  const visibleProcessed = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const byStatus = processedSorted.filter((d) => {
      const hasErrors = validateIssuedDoc(d).length > 0;
      if (statusFilter === "pending") return hasErrors;
      if (statusFilter === "ready") return !hasErrors;
      return true;
    });
    if (!query) return byStatus;
    return byStatus.filter((d) =>
      [d.invoiceNumber, d.series, d.customerName, d.customerTaxId, d.date, d.file?.name]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [processedSorted, searchQuery, statusFilter]);

  const globalSearchItems = useMemo<SearchableItem[]>(
    () =>
      processedSorted.map((d) => ({
        id: d.id,
        title: `${d.invoiceNumber || "(sin nº)"} · ${d.customerName || "(sin cliente)"}`,
        entityName: d.customerName || "",
        taxId: d.customerTaxId || "",
        subtitleParts: [d.date, d.customerTaxId, d.series, d.file?.name],
        totalAmount: parseNumericLike(d.totalAmount),
        searchValue: [d.invoiceNumber, d.series, d.customerName, d.customerTaxId, d.date, d.file?.name]
          .filter(Boolean)
          .join(" ")
          .toLowerCase(),
      })),
    [processedSorted],
  );


  const openDocData = openId ? processed.find((d) => d.id === openId) ?? null : null;

  useKeyboardShortcuts([
    { key: "k", ctrl: true, handler: () => setGlobalSearchOpen(true) },
    { key: "Escape", handler: () => setGlobalSearchOpen(false), allowInInputs: true, preventDefault: false },
  ]);


  /** Descarga los PDF renombrados AAAAMMDD_CLIENTE_SERIE_NºFACTURA en un ZIP. */
  const handleDownloadPdfs = async () => {
    if (processedSorted.length === 0) return;
    setDownloadingPdfs(true);
    try {
      const used = new Map<string, number>();
      const items = processedSorted.flatMap((doc) => {
        const base = buildIssuedFileName(doc);
        const count = used.get(base) ?? 0;
        used.set(base, count + 1);
        let invoiceName = base;
        if (count > 0) {
          const dot = base.lastIndexOf(".");
          invoiceName = dot > 0 ? `${base.slice(0, dot)}_${count + 1}${base.slice(dot)}` : `${base}_${count + 1}`;
        }
        const annexBase = invoiceName.replace(/\.[^.]+$/, "");
        const annexes = (doc.attachments ?? []).map((f, i) => ({
          file: f,
          name: `${annexBase}_anexo_${i + 1}_${f.name}`,
        }));
        return [{ file: doc.file, name: invoiceName }, ...annexes];
      });
      const slug = (company?.name || "emitidas").replace(/[^A-Za-z0-9]+/g, "_").slice(0, 40);
      await downloadFilesAsZip(items, `Facturas_emitidas_${slug}.zip`);
      toast({ title: "ZIP generado", description: `${processedSorted.length} factura(s) con sus anexos` });
    } catch (e) {
      toast({
        title: "Error generando el ZIP",
        description: e instanceof Error ? e.message : "Error desconocido",
        variant: "destructive",
      });
    } finally {
      setDownloadingPdfs(false);
    }
  };

  const handleExport = async () => {
    if (!company?.a3_company_code) {
      toast({
        title: "La empresa no tiene código A3",
        description: "Configura el código A3 antes de exportar.",
        variant: "destructive",
      });
      return;
    }
    if (processed.length === 0) return;
    if (totalErrors > 0) {
      toast({
        title: "Corrige los avisos antes de exportar",
        description: `${totalErrors} dato(s) pendiente(s) en las facturas`,
        variant: "destructive",
      });
      return;
    }
    setGenerating(true);
    try {
      const result = buildIssuedSuenlaceFile(processedSorted.map(toIssuedRow), {
        companyCode: company.a3_company_code,
      });
      const dat = new Blob([result.bytes as BlobPart], { type: "application/octet-stream" });
      const lastDot = result.fileName.lastIndexOf(".");
      const zipBase = lastDot > 0 ? result.fileName.slice(0, lastDot) : result.fileName;
      const excel = await buildExcelSummary(processedSorted);
      await downloadFilesAsZip(
        [
          { file: dat, name: result.fileName },
          { file: excel, name: `${zipBase}.xlsx` },
        ],
        `${zipBase}.zip`,
      );
      toast({
        title: "SUENLACE generado",
        description: `${result.invoiceCount} factura(s) · ${result.lineCount} registro(s)`,
      });
    } catch (e) {
      toast({
        title: "Error generando SUENLACE",
        description: e instanceof Error ? e.message : "Error desconocido",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  if (sessionLoading) {
    return (
      <div className="flex min-h-[calc(100vh-2.25rem)] items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Cargando sesión…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-2.25rem)] bg-background flex flex-col">
      <header className="border-b bg-primary">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-6 py-4 flex-wrap">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--brand-green))]">
            <FileText className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-primary-foreground truncate">
              Facturas emitidas desde PDF
            </h1>
            <p className="text-xs text-primary-foreground/60 truncate">
              {company ? company.name : "Lee tus facturas de venta y genera el SUENLACE para A3"}
            </p>
          </div>
          {company && (
            <Button
              variant="outline"
              size="sm"
              className="gap-2 text-xs"
              onClick={handleRefreshMaster}
              disabled={refreshingMaster}
            >
              {refreshingMaster ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Actualizar datos
            </Button>
          )}
          {company && (
            <Button variant="outline" size="sm" className="gap-2 text-xs" onClick={() => setShowCompanySelector(true)}>
              <Building2 className="h-3.5 w-3.5" />
              Cambiar empresa
            </Button>
          )}

          {(processed.length > 0 || failed.length > 0) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmFinalize(true)}
              className="gap-2 text-xs text-destructive border-destructive/30 hover:bg-destructive/10"
            >
              <CheckCircle className="h-3 w-3" />
              Finalizar
            </Button>
          )}
          <ThemeToggle />
          <Button variant="outline" size="sm" className="gap-2 text-xs" onClick={signOut}>
            <LogOut className="h-3.5 w-3.5" />
            Salir
          </Button>
        </div>
      </header>



      <main className="flex-1">
        <div className="mx-auto max-w-7xl px-6 py-6 space-y-6">
          {!company ? (
            <div className="rounded-lg border-2 border-dashed bg-card p-10 text-center">
              <Building2 className="mx-auto h-10 w-10 text-muted-foreground/60" />
              <p className="mt-3 text-sm text-muted-foreground">Elige la empresa emisora para empezar.</p>
              <Button className="mt-4 gap-2" onClick={() => setShowCompanySelector(true)}>
                <Building2 className="h-4 w-4" />
                Elegir empresa
              </Button>
            </div>
          ) : (
            <>
              <DropZone onFilesSelect={handleFiles} disabled={pending > 0} />

              {pending > 0 && (
                <div className="flex items-center gap-2 rounded-md border bg-card px-4 py-3 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Procesando {pending} documento(s)…
                </div>
              )}

              {failed.length > 0 && (
                <section className="rounded-lg border bg-card overflow-hidden">
                  <div className="border-b bg-muted/30 px-4 py-3">
                    <h2 className="text-sm font-semibold">{failed.length} documento(s) sin procesar</h2>
                  </div>
                  <ul className="divide-y">
                    {failed.map((f) => (
                      <li key={f.id} className="flex items-center gap-3 px-4 py-3 flex-wrap">
                        <AlertTriangle className={`h-4 w-4 shrink-0 ${f.kind === "rejected" ? "text-muted-foreground" : "text-destructive"}`} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{f.file.name}</p>
                          <p className="text-xs text-muted-foreground">{f.reason}</p>
                        </div>
                        <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => retryFailed(f)}>
                          <RefreshCw className="h-3.5 w-3.5" /> Reintentar
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => setFailed((prev) => prev.filter((x) => x.id !== f.id))}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {processed.length > 0 && (
                <section className="rounded-lg border bg-card overflow-hidden">
                  <div className="border-b bg-muted/30 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                    <h2 className="text-sm font-semibold">{processed.length} factura(s) procesada(s)</h2>
                    <div className="flex items-center gap-3 flex-wrap">
                      <SaveIndicator status={saveStatus} lastSavedAt={lastSavedAt} />

                      {totalErrors === 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--brand-green))]">
                          <CheckCircle className="h-3.5 w-3.5" /> Listo para exportar
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-destructive">
                          <AlertTriangle className="h-3.5 w-3.5" /> {totalErrors} dato(s) pendiente(s)
                        </span>
                      )}
                      <Button
                        variant="outline"
                        className="gap-2"
                        onClick={handleDownloadPdfs}
                        disabled={downloadingPdfs}
                      >
                        {downloadingPdfs ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        Descargar PDF (ZIP)
                      </Button>
                      <Button
                        className="gap-2 bg-[hsl(var(--brand-green))] text-white hover:bg-[hsl(152,40%,45%)]"
                        onClick={handleExport}
                        disabled={generating || totalErrors > 0}
                      >
                        {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        Descargar ZIP SUENLACE
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 border-b p-4 text-xs">
                    <div><p className="text-muted-foreground">Facturas</p><p className="text-sm font-semibold">{processed.length}</p></div>
                    <div><p className="text-muted-foreground">Base</p><p className="text-sm font-semibold">{formatMoney(totals.base)}</p></div>
                    <div><p className="text-muted-foreground">IVA</p><p className="text-sm font-semibold">{formatMoney(totals.vat)}</p></div>
                    <div><p className="text-muted-foreground">Total</p><p className="text-sm font-semibold">{formatMoney(totals.total)}</p></div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 border-b p-4">
                    <DocStatusFilter value={statusFilter} onChange={setStatusFilter} counts={statusCounts} />
                    <div className="relative min-w-[220px] flex-1">
                      <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Buscar por cliente, CIF o nº factura…"
                        className="h-8 pl-9 text-xs"
                      />
                    </div>
                  </div>

                  <ul className="divide-y">
                    {visibleProcessed.map((d) => {

                      const errs = validateIssuedDoc(d);
                      return (
                        <li key={d.id} className={`flex items-center ${d.reviewed ? "bg-muted/40" : ""}`}>
                          <button
                            type="button"
                            onClick={() => openDoc(d.id)}
                            className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50"
                          >
                            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium">
                                {d.isCreditNote && (
                                  <span
                                    className="mr-2 rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-semibold text-white align-middle"
                                    title={
                                      d.creditNoteHints?.length
                                        ? `Abono / rectificativa: ${d.creditNoteHints.join(", ")}`
                                        : "Factura de abono / rectificativa: los importes van en negativo."
                                    }
                                  >
                                    ABONO
                                  </span>
                                )}
                                {d.invoiceNumber || "(sin nº)"} · {d.customerName || "(sin cliente)"}
                              </p>
                              <p className="truncate text-xs text-muted-foreground">
                                {d.date || "sin fecha"} · {d.customerTaxId || "sin NIF"} · {d.file.name}
                              </p>
                            </div>
                            <span className="text-sm font-semibold whitespace-nowrap">
                              {formatMoney(parseNumericLike(d.totalAmount))}
                            </span>
                            {errs.length > 0 ? (
                              <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
                            ) : (
                              <CheckCircle className="h-4 w-4 shrink-0 text-[hsl(var(--brand-green))]" />
                            )}
                          </button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="mr-2 h-8 w-8 shrink-0 text-destructive hover:bg-destructive/10"
                            title="Eliminar documento"
                            onClick={() => deleteProcessed(d.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </li>
                      );
                    })}
                  </ul>

                </section>
              )}
            </>
          )}
        </div>
      </main>

      <GlobalSearch
        open={globalSearchOpen}
        onOpenChange={setGlobalSearchOpen}
        items={globalSearchItems}
        entityLabel="cliente"
        onSelectItem={(id) => {
          openDoc(id);
          setGlobalSearchOpen(false);
        }}
      />


      <MasterSelector
        open={showCompanySelector}
        onSelect={(c) => {
          setShowCompanySelector(false);
          if (c.id === company?.id) return;
          const hasDocuments = processed.length > 0 || failed.length > 0;
          if (hasDocuments && company) {
            // Cambiar de empresa con documentos en curso requiere confirmación:
            // se vacía la sesión (igual que en facturas recibidas).
            setPendingCompanyChange(c);
            return;
          }
          companySelectedManuallyRef.current = true;
          setCompany(c);
          void saveSession(
            { processed, failed, companyId: null },
            { immediate: true },
          );
        }}
        onCancel={() => setShowCompanySelector(false)}
      />

      <AlertDialog
        open={pendingCompanyChange !== null}
        onOpenChange={(v) => !v && setPendingCompanyChange(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cambiar de empresa</AlertDialogTitle>
            <AlertDialogDescription>
              Tienes {processed.length + failed.length} factura
              {processed.length + failed.length === 1 ? "" : "s"} en la sesión actual de{" "}
              <strong>{company?.name}</strong>. Cambiar a{" "}
              <strong>{pendingCompanyChange?.name}</strong> reiniciará la sesión y eliminará
              esas facturas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingCompanyChange(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                const target = pendingCompanyChange;
                setPendingCompanyChange(null);
                if (!target) return;
                isResettingCompanyRef.current = true;
                try {
                  await clearSession();
                } catch (e) {
                  console.error("clearSession failed:", e);
                }
                setProcessed([]);
                setFailed([]);
                setOpenId(null);
                setPending(0);
                companySelectedManuallyRef.current = true;
                setCompany(target);
                window.setTimeout(() => {
                  isResettingCompanyRef.current = false;
                }, 0);
              }}
            >
              Cambiar y restablecer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <IssuedAIValidator processed={processed} onOpenInvoice={openDoc} />

      {openDocData && (
      <IssuedInvoiceSplitView
        doc={openDocData}
        index={openId ? processedSorted.findIndex((d) => d.id === openId) : 0}
        total={processedSorted.length}
        onChange={(patch) => openId !== null && updateDoc(openId, patch)}
        onClose={() => setOpenId(null)}
        onPrev={() => setOpenId((id) => {
          if (!id) return null;
          const idx = processedSorted.findIndex((d) => d.id === id);
          const next = (idx - 1 + processedSorted.length) % processedSorted.length;
          return processedSorted[next].id;
        })}
        onNext={() => setOpenId((id) => {
          if (!id) return null;
          const idx = processedSorted.findIndex((d) => d.id === id);
          const next = (idx + 1) % processedSorted.length;
          return processedSorted[next].id;
        })}
        onAddCustomer={() => setCustomerDialogOpen(true)}
        onDelete={() => openDocData && deleteProcessed(openDocData.id)}
        accountCatalog={accountCatalog.map((a) => ({ account: a.account, description: a.description }))}
        companyId={company?.id ?? null}
        onAccountCreated={(account, description) =>
          setAccountCatalog((prev) =>
            prev.some((a) => a.account === account)
              ? prev
              : [...prev, { id: `${account}-new`, account, description }].sort((a, b) => a.account.localeCompare(b.account)),
          )
        }
        masterOptions={Array.from(master.entries()).map(([taxId, e]) => ({
          taxId,
          accountCode: e.accountCode,
          salesAccount: e.salesAccount,
          description: e.description,
          postalCode: e.postalCode,
        }))}
      />
      )}

      <QuickCustomerDialog
        open={customerDialogOpen}
        companyId={company?.id ?? null}
        initial={{
          taxId: openDocData?.customerTaxId || "",
          description: openDocData?.customerName || "",
          accountCode: openDocData?.customerAccount || "",
          salesAccount: openDocData?.salesAccount || "",
          postalCode: openDocData?.postalCode || "",
          operationType: "interior",
        }}
        onClose={() => setCustomerDialogOpen(false)}
        onSaved={async (record: CustomerMasterRecord) => {
          const map = await refreshMaster(company?.id);
          setProcessed((prev) => prev.map((d) =>
            normalizeNif(d.customerTaxId) === normalizeNif(record.taxId)
              ? applySeriesRules(applyCustomerMaster(d, map), issuerTaxIdRef.current)
              : d,
          ));
        }}
      />

      <AlertDialog open={confirmFinalize} onOpenChange={setConfirmFinalize}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Finalizar sesión</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminarán las {processed.length} factura(s) procesada(s)
              {failed.length > 0 ? ` y los ${failed.length} documento(s) sin procesar` : ""} de la
              sesión guardada. Asegúrate de haber descargado el ZIP SUENLACE antes de continuar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleFinalize}>Finalizar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

};

export default ProcesarPdf;
